import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, CheckCheck, Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

const TYPE_ICONS = { info: Info, warning: AlertTriangle, success: CheckCircle, error: XCircle };
const TYPE_COLORS = {
  info: 'text-blue-500 bg-blue-500/10',
  warning: 'text-amber-500 bg-amber-500/10',
  success: 'text-emerald-500 bg-emerald-500/10',
  error: 'text-red-500 bg-red-500/10'
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?unread_only=false');
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (err) { /* notification fetch non-critical */ }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-read');
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { /* mark-read non-critical */ }
  };

  const markOneRead = async (id) => {
    try {
      await api.post(`/notifications/mark-read?notification_id=${id}`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (err) { /* mark-read non-critical */ }
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <Button variant="ghost" size="icon" className="relative h-9 w-9" onClick={() => setOpen(!open)} data-testid="notification-bell-btn">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-card border border-border rounded-xl shadow-xl z-50 animate-fade-in" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <span className="font-heading text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-primary" onClick={markAllRead} data-testid="mark-all-read-btn">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </Button>
            )}
          </div>
          <ScrollArea className="max-h-80">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No notifications</div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.slice(0, 20).map(n => {
                  const Icon = TYPE_ICONS[n.type] || Info;
                  const colorClass = TYPE_COLORS[n.type] || TYPE_COLORS.info;
                  return (
                    <div key={n.id}
                      className={`p-3 hover:bg-muted/50 transition-colors cursor-pointer ${!n.read ? 'bg-primary/5' : ''}`}
                      onClick={() => !n.read && markOneRead(n.id)}
                      data-testid={`notification-item-${n.id}`}>
                      <div className="flex gap-2.5">
                        <div className={`w-7 h-7 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs font-semibold truncate">{n.title}</p>
                            {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[9px] text-muted-foreground mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
